import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { AreaCreate, AreaDetail, ContactPickItem } from '@tww/shared';
import { fmtCoords, parseCoords, suggestAreaName } from '@tww/shared';
import { api, ApiRequestError } from '../../api/client';
import { useLookups } from '../../api/hooks';
import { ContactPicker } from '../../ui/ContactPicker';
import { ErrorState, Note, Skeleton } from '../../ui/basics';
import { ComboField, NumberInput, Select, TextArea, TextInput } from '../../ui/fields';
import { useToast } from '../../ui/Toast';

interface F {
  name: string;
  status: 'pool' | 'lead';
  state_code: string;
  region: string;
  city: string;
  zip: string;
  municipality: string;
  district: string;
  flur: string;
  parcel: string;
  compartment: string;
  coords: string;
  hectares: number | null;
  forester: ContactPickItem | null;
  owner: ContactPickItem | null;
  owner_name_text: string;
  ownership_type: string;
  certification: string;
  water_protection: string;
  soil: string;
  wildlife_protection: string;
  ecological_need: string;
  neighbour_potential: string;
  rescue_point: string;
  access_note: string;
  planting_season_code: string;
  planting_year: number | null;
  trees_planted: number | null;
  notes: string;
}
const pick = (c: { id: number; display_name: string; rating_grade?: any } | null): ContactPickItem | null => (c ? { id: c.id, display_name: c.display_name, sub: null, rating_grade: c.rating_grade ?? null, rating_reason: null, city: null, region: null, last_price: null, categories: [] } : null);
const from = (a: AreaDetail | null): F => ({
  name: a?.name ?? '',
  status: 'pool',
  state_code: a?.state_code ?? '',
  region: a?.region ?? '',
  city: a?.city ?? '',
  zip: a?.zip ?? '',
  municipality: a?.municipality ?? '',
  district: a?.district ?? '',
  flur: a?.flur ?? '',
  parcel: a?.parcel ?? '',
  compartment: a?.compartment ?? '',
  coords: fmtCoords(a?.lat, a?.lng),
  hectares: a?.hectares ?? null,
  forester: pick(a?.forester ?? null),
  owner: pick(a?.owner ?? null),
  owner_name_text: a?.owner_name_text ?? '',
  ownership_type: a?.ownership_type ?? '',
  certification: a?.certification ?? '',
  water_protection: a?.water_protection ?? '',
  soil: a?.soil ?? '',
  wildlife_protection: a?.wildlife_protection ?? '',
  ecological_need: a?.ecological_need ?? '',
  neighbour_potential: a?.neighbour_potential ?? '',
  rescue_point: a?.rescue_point ?? '',
  access_note: a?.access_note ?? '',
  planting_season_code: a?.planting_season_code ?? '',
  planting_year: a?.planting_year ?? null,
  trees_planted: a?.trees_planted ?? null,
  notes: a?.notes ?? '',
});
const nn = (s: string) => (s.trim() ? s.trim() : null);

export function AreaFormPage() {
  const { id } = useParams();
  const aid = id ? Number(id) : null;
  const q = useQuery({ queryKey: ['area', aid], queryFn: () => api.get<AreaDetail>(`/areas/${aid}`), enabled: aid != null });
  if (aid != null && q.isLoading) return <Skeleton rows={8} />;
  if (aid != null && (q.isError || !q.data)) return <ErrorState error={q.error ?? new Error('Fläche nicht gefunden')} />;
  return <Inner key={aid ?? 'new'} area={q.data ?? null} />;
}

function Inner({ area }: { area: AreaDetail | null }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const lookups = useLookups();
  const [f, setF] = useState<F>(() => from(area));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof F>(k: K, v: F[K]) => setF((s) => ({ ...s, [k]: v }));
  const opts = (key: string) => (lookups.data?.find((l) => l.key === key)?.values ?? []).filter((v) => v.is_active).map((v) => ({ value: v.code, label: v.label }));
  const coords = parseCoords(f.coords);
  // nicht lesbare Koordinaten blockieren das Speichern, sonst würden vorhandene Werte mit leer überschrieben
  const coordErr = f.coords.trim() && !coords ? 'Koordinaten nicht lesbar, Beispiel: 50.318203, 8.621344' : undefined;
  const coordWarn = coords && (coords.lat < 47 || coords.lat > 55 || coords.lng < 5 || coords.lng > 16) ? 'Liegt außerhalb Deutschlands, bitte prüfen' : undefined;
  const submit = () => {
    if (coordErr) {
      setErrors({ coords: coordErr });
      toast('Koordinaten prüfen', 'error');
      return;
    }
    save.mutate();
  };
  const save = useMutation({
    mutationFn: () => {
      const body: AreaCreate = {
        name: f.name.trim() || suggestAreaName(f),
        status: f.status,
        state_code: nn(f.state_code),
        region: nn(f.region),
        city: nn(f.city),
        zip: nn(f.zip),
        municipality: nn(f.municipality),
        district: nn(f.district),
        flur: nn(f.flur),
        parcel: nn(f.parcel),
        compartment: nn(f.compartment),
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
        hectares: f.hectares,
        forester_contact_id: f.forester?.id ?? null,
        owner_contact_id: f.owner?.id ?? null,
        owner_name_text: nn(f.owner_name_text),
        ownership_type: nn(f.ownership_type),
        certification: nn(f.certification),
        water_protection: nn(f.water_protection),
        soil: nn(f.soil),
        wildlife_protection: nn(f.wildlife_protection),
        ecological_need: nn(f.ecological_need),
        neighbour_potential: nn(f.neighbour_potential),
        rescue_point: nn(f.rescue_point),
        access_note: nn(f.access_note),
        planting_season_code: (f.planting_season_code || null) as AreaCreate['planting_season_code'],
        planting_year: f.planting_year,
        trees_planted: f.trees_planted,
        notes: nn(f.notes),
      };
      if (area) {
        const { status: _s, ...patch } = body;
        return api.patch<AreaDetail>(`/areas/${area.id}`, { ...patch, updated_at: area.updated_at });
      }
      return api.post<AreaDetail>('/areas', body);
    },
    onSuccess: (a) => {
      qc.invalidateQueries({ queryKey: ['areas'] });
      qc.invalidateQueries({ queryKey: ['area', a.id] });
      toast(area ? 'Fläche gespeichert' : 'Fläche angelegt');
      nav(`/flaechen/${a.id}`);
    },
    onError: (e) => {
      if (e instanceof ApiRequestError) {
        setErrors(e.fields ?? {});
        toast(e.message, 'error');
      } else toast('Speichern fehlgeschlagen', 'error');
    },
  });
  const states = opts('states');
  const regions = (lookups.data?.find((l) => l.key === 'regions')?.values ?? []).filter((r) => r.is_active && (!f.state_code || !r.meta?.state || r.meta.state === f.state_code));
  const back = area ? `/flaechen/${area.id}` : '/flaechen';
  return (
    <>
      <div className="crumb">
        <Link to="/flaechen">Flächen</Link>
        {area && <> / <Link to={back}>{area.name}</Link></>}
      </div>
      <div className="head">
        <div>
          <h1>{area ? 'Fläche bearbeiten' : 'Neue Fläche'}</h1>
          {area && <div className="sub">{area.name}</div>}
        </div>
      </div>
      <form className="page-form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div className="col">
          <section className="section">
            <div className="sh"><h2>Lage</h2></div>
            <div className="sb">
              <div className="form">
                <TextInput full label="Name" value={f.name} onChange={(v) => set('name', v)} placeholder={suggestAreaName(f) !== 'Fläche' ? `Vorschlag: ${suggestAreaName(f)}` : 'z. B. Töpsheide, Abt. 14b'} error={errors.name} />
                <TextInput label="Ort der Pflanzfläche" value={f.city} onChange={(v) => set('city', v)} autoFocus={!area} />
                <TextInput label="Gemeinde" value={f.municipality} onChange={(v) => set('municipality', v)} />
                <TextInput label="Gemarkung" value={f.district} onChange={(v) => set('district', v)} />
                <TextInput label="PLZ" value={f.zip} onChange={(v) => set('zip', v)} />
                <div className="form three full">
                  <TextInput label="Flur" value={f.flur} onChange={(v) => set('flur', v)} />
                  <TextInput label="Flurstück" value={f.parcel} onChange={(v) => set('parcel', v)} />
                  <TextInput label="Abteilung" value={f.compartment} onChange={(v) => set('compartment', v)} />
                </div>
                <TextInput label="Koordinaten" value={f.coords} onChange={(v) => { set('coords', v); setErrors(({ coords: _c, ...rest }) => rest); }} placeholder="50.318203, 8.621344" error={coordErr} hint={coordWarn} />
                <NumberInput label="Hektar" decimals={2} value={f.hectares} onChange={(v) => set('hectares', v)} />
                <Select label="Bundesland" value={f.state_code} onChange={(v) => set('state_code', v)} options={states} />
                <ComboField label="Region" value={f.region} onChange={(v) => set('region', v)} options={regions.map((r) => ({ value: r.code, label: r.label }))} newHint="wird als neue Region gespeichert" />
                {!area && (
                  <div className="field">
                    <label>Anlegen als</label>
                    <div className="seg" role="radiogroup">
                      <button type="button" role="radio" aria-checked={f.status === 'pool'} className={f.status === 'pool' ? 'on' : ''} onClick={() => set('status', 'pool')}>Poolfläche</button>
                      <button type="button" role="radio" aria-checked={f.status === 'lead'} className={f.status === 'lead' ? 'on' : ''} onClick={() => set('status', 'lead')}>Lead</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
          <section className="section">
            <div className="sh"><h2>Zuständige</h2></div>
            <div className="sb">
              <div className="form">
                <ContactPicker label="Förster" category="forester" value={f.forester} onChange={(v) => set('forester', v)} />
                <ContactPicker label="Waldbesitzer" category="owner" value={f.owner} onChange={(v) => set('owner', v)} />
                {!f.owner && <TextInput label="Waldbesitzer als Text" value={f.owner_name_text} onChange={(v) => set('owner_name_text', v)} hint="Wenn noch kein Kontakt angelegt ist" />}
                <Select label="Waldbesitzart" value={f.ownership_type} onChange={(v) => set('ownership_type', v)} options={opts('ownership_types')} />
              </div>
            </div>
          </section>
          <section className="section">
            <div className="sh"><h2>Forstliche Angaben</h2></div>
            <div className="sb">
              <div className="form">
                <Select label="Zertifizierung" value={f.certification} onChange={(v) => set('certification', v)} options={opts('certifications')} />
                <TextInput label="Wasserschutzgebiet, Zone" value={f.water_protection} onChange={(v) => set('water_protection', v)} />
                <Select label="Wildschutz" value={f.wildlife_protection} onChange={(v) => set('wildlife_protection', v)} options={opts('wildlife_protection')} />
                <TextInput label="Rettungspunkt" value={f.rescue_point} onChange={(v) => set('rescue_point', v)} />
                <TextArea label="Bodenverhältnisse" value={f.soil} onChange={(v) => set('soil', v)} rows={2} />
                <TextArea label="Ökologische Notwendigkeit / Schadereignis" value={f.ecological_need} onChange={(v) => set('ecological_need', v)} rows={2} />
                <TextArea label="Pflanzpotenzial auf Nachbarflächen" value={f.neighbour_potential} onChange={(v) => set('neighbour_potential', v)} rows={2} />
                <TextArea label="Zuwegung" value={f.access_note} onChange={(v) => set('access_note', v)} rows={2} />
              </div>
            </div>
          </section>
          <section className="section">
            <div className="sh"><h2>Pflanzung</h2></div>
            <div className="sb">
              <div className="form three">
                <Select label="Saison" value={f.planting_season_code} onChange={(v) => set('planting_season_code', v)} options={[{ value: 'F', label: 'Frühjahr' }, { value: 'S', label: 'Sommer' }, { value: 'H', label: 'Herbst' }]} />
                <NumberInput label="Pflanzjahr" value={f.planting_year} onChange={(v) => set('planting_year', v)} />
                <NumberInput label="Bäume gepflanzt" value={f.trees_planted} onChange={(v) => set('trees_planted', v)} />
              </div>
            </div>
          </section>
          <section className="section">
            <div className="sh"><h2>Notizen</h2></div>
            <div className="sb"><div className="form one"><TextArea label="" value={f.notes} onChange={(v) => set('notes', v)} rows={3} /></div></div>
          </section>
        </div>
        <aside className="aside">
          <div className="section">
            <div className="sh"><h2>Zusammenfassung</h2></div>
            <div className="sb summary-box">
              <strong style={{ fontSize: 16 }}>{f.name.trim() || suggestAreaName(f)}</strong>
              <dl>
                <dt>Lage</dt><dd>{[f.municipality, states.find((s) => s.value === f.state_code)?.label, f.region].filter(Boolean).join(', ') || '–'}</dd>
                <dt>Größe</dt><dd>{f.hectares != null ? `${String(f.hectares).replace('.', ',')} ha` : '–'}</dd>
                <dt>Förster</dt><dd>{f.forester?.display_name ?? '–'}</dd>
                <dt>Waldbesitzer</dt><dd>{f.owner?.display_name ?? (f.owner_name_text || '–')}</dd>
              </dl>
              {Object.keys(errors).length > 0 && <Note kind="bad">{Object.values(errors).join(' · ')}</Note>}
            </div>
          </div>
          <div className="actions-col">
            <button className="btn primary" type="submit" disabled={save.isPending || Boolean(coordErr)}>{area ? 'Änderungen speichern' : 'Fläche anlegen'}</button>
            <Link className="btn" to={back}>Abbrechen</Link>
          </div>
        </aside>
      </form>
    </>
  );
}
